import { useEffect, useRef } from 'react';

import type { PublicLanguage, PublicReportChapter } from '@/entities/publicReport';

import type { PublicCopy } from '../copy';
import { jumpTo } from '../jump';
import type { FullReportIndex, IndexedEntry } from '../reportIndex';
import { NhgAnnex } from './NhgAnnex';

export interface NhgTabProps {
    chapter: Extract<PublicReportChapter, { kind: 'nhg' }>;
    index: FullReportIndex;
    language: PublicLanguage;
    copy: PublicCopy;
    /** `1` op de publieke pagina; `2` in de kantoorpreview. */
    headingLevel: 1 | 2;
    /** De regel waar een NHG-code onder een bevinding heen wijst; `null` zonder sprong. */
    target: { anchor: string; serial: number } | null;
    /** De sprong is gedaan. */
    onTargetDone: () => void;
    /** Van een regel naar een bevinding in het rapport. */
    onEntryJump: (indexed: IndexedEntry) => void;
}

/**
 * Het tabblad NHG: de bijlage van het Model bouwkundig rapport NHG, los van het
 * rapport, zoals een geldverstrekker hem leest. Een NHG-code onder een
 * bevinding opent dit tabblad bij zijn regel; een regel wijst terug naar zijn
 * bevindingen in het rapport.
 */
export function NhgTab({
    chapter,
    index,
    language,
    copy,
    headingLevel,
    target,
    onTargetDone,
    onEntryJump,
}: NhgTabProps) {
    const Heading = headingLevel === 1 ? 'h1' : 'h2';
    const release = useRef<() => void>(() => undefined);

    useEffect(() => {
        const current = release;

        return () => current.current();
    }, []);

    useEffect(() => {
        if (target === null) {
            return;
        }

        release.current();
        release.current = jumpTo(target.anchor, { hold: true });
        onTargetDone();
    }, [target, onTargetDone]);

    return (
        <div className="px-[var(--pr-pad-x)] pt-7 pb-8">
            <Heading className="m-0 mb-5 font-[family-name:var(--font-display)] text-[clamp(22px,4cqi,26px)] leading-[1.15] font-semibold tracking-[-0.02em] text-pretty text-[var(--text-primary)]">
                {chapter.title}
            </Heading>

            <NhgAnnex
                annex={chapter.annex}
                level={headingLevel}
                language={language}
                copy={copy}
                index={index}
                onEntryJump={onEntryJump}
            />
        </div>
    );
}
